import React, { useEffect } from 'react';
import type { FormInstance } from 'antd';
import { Button, Col, DatePicker, Form, Input, Row, Select } from 'antd';
import { Plus, Trash2, ChevronDown } from 'lucide-react';
import dayjs from 'dayjs';
import { useAuth } from '@/contexts/AuthContext';
import { usersService } from '@/services/users.service';
import styles from '../../pages/UpdatePersonalProfilePage.module.css';

const { Option } = Select;

const PARTY_FORMS: Array<{ value: string; label: string }> = [
    { value: 'join', label: 'Kết nạp dự bị' },
    { value: 'join-official', label: 'Kết nạp chính thức'},
    { value: 'other', label: 'Khác' }
];

interface UpdatePartyHistorySectionProps {
  /**
   * userId của nhân sự cần hiển thị lịch sử đảng viên.
   * - Nếu truyền vào: ưu tiên dùng userId này (dùng cho màn Quản lý nhân sự chỉnh hồ sơ staff).
   * - Nếu không truyền: fallback về user hiện tại từ AuthContext (dùng cho trang hồ sơ cá nhân tự cập nhật).
   */
  userId?: number;
  form: FormInstance;
}

export const UpdatePartyHistorySection: React.FC<UpdatePartyHistorySectionProps> = ({ userId, form }) => {
  const { user } = useAuth();

  // Fetch lịch sử đảng viên (bảng user_party_history)
  useEffect(() => {
    const fetchPartyHistory = async () => {
      // Ưu tiên userId truyền từ props (màn quản lý nhân sự),
      // nếu không có thì fallback sang user hiện tại (trang hồ sơ cá nhân).
      let targetUserId: number | null = null;

      if (typeof userId === 'number') {
        targetUserId = userId;
      } else if (user?.id) {
        targetUserId = parseInt(user.id.toString(), 10);
      }

      if (!targetUserId || Number.isNaN(targetUserId)) return;

      try {
        const partyHistories = await usersService.getPartyHistory(targetUserId).catch(() => []);

        // Map danh sách lịch sử đảng viên vào Form.List "partyHistories"
        const mappedPartyHistories = (partyHistories || []).map((party: any) => ({
          cardNumber: party.cardNumber || '',
          form: party.form || '',
          fromDate: party.fromDate ? dayjs(party.fromDate) : undefined,
          toDate: party.toDate ? dayjs(party.toDate) : undefined,
          admissionPlace: party.admissionPlace || '',
          transferPlace: party.transferPlace || '',
        }));

        form.setFieldsValue({
          partyHistories: mappedPartyHistories,
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching party history in UpdatePartyHistorySection:', error);
      }
    };

    fetchPartyHistory();
  }, [form, userId, user?.id]);
  return (
    <div className={styles.section}>
      <Form.List name="partyHistories">
        {(fields, { add, remove }) => (
          <div className={styles.familySection}>
            {fields.length === 0 && (
              <div className={styles.familyEmptyState}>
                <div style={{ marginBottom: 16 }}>
                  Chưa có lịch sử nào. Nhấn &quot;Thêm lịch sử&quot; để bắt đầu.
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    type="primary"
                    icon={<Plus size={16} />}
                    size="middle"
                    className={styles.familyAddButton}
                    onClick={() => add({})}
                  >
                    Thêm lịch sử
                  </Button>
                </div>
              </div>
            )}

            {fields.map((field, index) => (
              <div key={field.key} className={styles.workCard}>
                <div className={styles.workCardHeader}>
                  <span className={styles.workCardSubtitle}>Lịch sử {index + 1}</span>
                  <Button
                    type="text"
                    danger
                    icon={<Trash2 size={16} />}
                    className={styles.workCardRemoveButton}
                    onClick={() => remove(field.name)}
                  >
                    Xóa
                  </Button>
                </div>

                <Row gutter={24} className={styles.formRow}>
                  <Col span={8}>
                    <Form.Item name={[field.name, 'cardNumber']} label="Số thẻ">
                      <Input
                        placeholder="Số thẻ"
                        size="large"
                        className={styles.input}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item name={[field.name, 'form']} label="Hình thức">
                      <Select
                        placeholder="Chọn hình thức"
                        size="large"
                        className={styles.select}
                        suffixIcon={
                          <ChevronDown
                            size={16}
                            className={styles.dropdownIcon}
                          />
                        }
                        allowClear
                      >
                        {PARTY_FORMS.map((opt) => (
                          <Option key={opt.value} value={opt.value}>
                          {opt.label}
                        </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={4}>
                    <Form.Item name={[field.name, 'fromDate']} label="Từ ngày">
                      <DatePicker
                        placeholder="Chọn ngày"
                        size="large"
                        format="DD/MM/YYYY"
                        className={styles.fullWidth}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={4}>
                    <Form.Item name={[field.name, 'toDate']} label="Đến ngày">
                      <DatePicker
                        placeholder="Chọn ngày"
                        size="large"
                        format="DD/MM/YYYY"
                        className={styles.fullWidth}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={24} className={styles.formRow}>
                  <Col span={12}>
                    <Form.Item name={[field.name, 'admissionPlace']} label="Nơi kết nạp">
                      <Input
                        placeholder="Nơi kết nạp"
                        size="large"
                        className={styles.input}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name={[field.name, 'transferPlace']} label="Nơi điều chuyển">
                      <Input
                        placeholder="Nơi điều chuyển"
                        size="large"
                        className={styles.input}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </div>
            ))}

            {/* Nút Thêm lịch sử ở cuối danh sách */}
            {fields.length > 0 && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                <Button
                  type="primary"
                  icon={<Plus size={16} />}
                  size="middle"
                  className={styles.familyAddButton}
                  onClick={() => add({})}
                >
                  Thêm lịch sử
                </Button>
              </div>
            )}
          </div>
        )}
      </Form.List>
    </div>
  );
};

